import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, HeadlineL } from '@sorare/core/src/atoms/typography';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';

import { MarketGuidanceLayout } from 'components/onboarding/MarketGuidanceLayout';

import type { TeamsConfirmation_teamInterface } from './__generated__/index.graphql';

const StyledImg = styled.img`
  width: 36px;
  height: 36px;
  object-fit: contain;
`;

type Props = {
  selectedTeams: TeamsConfirmation_teamInterface[];
  onSubmit?: () => void;
};
export const TeamsConfirmation = ({ selectedTeams, onSubmit }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();

  return (
    <MarketGuidanceLayout
      cta={
        <Button
          fullWidth={!isTabletAndAbove}
          color="secondary"
          size="medium"
          onClick={onSubmit}
        >
          <FormattedMessage
            id="TeamsConfirmation.cta"
            defaultMessage="View Recommended Players"
          />
        </Button>
      }
    >
      <Vertical center={isTabletAndAbove} gap={2}>
        <Vertical center={isTabletAndAbove}>
          <HeadlineL>
            <FormattedMessage
              id="TeamsConfirmation.title"
              defaultMessage="Nice picks!"
            />
          </HeadlineL>
          <BodyL color="var(--c-nd-600)">
            <FormattedMessage
              id="TeamsConfirmation.subtitle"
              defaultMessage="Your football knowledge is the key to success on Sorare. Let's leverage this knowledge by focusing on players from these clubs:"
            />
          </BodyL>
        </Vertical>
        <Vertical gap={2}>
          {selectedTeams.map(({ slug, name, pictureUrl }) => (
            <Horizontal key={slug} gap={1.5}>
              {pictureUrl && <StyledImg src={pictureUrl} alt="" />}
              <BodyL bold>{name}</BodyL>
            </Horizontal>
          ))}
        </Vertical>
      </Vertical>
    </MarketGuidanceLayout>
  );
};

TeamsConfirmation.fragments = {
  teamInterface: gql`
    fragment TeamsConfirmation_teamInterface on TeamInterface {
      slug
      name
      pictureUrl
    }
  ` as TypedDocumentNode<TeamsConfirmation_teamInterface>,
};
